import * as Sentry from "@sentry/react-native";
import { TEST_ID_AI_PLAYER_SUMMARY } from "e2e/testId";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  GenerationMetadata,
  useSSEGeneration,
} from "@/features/player-detail/hooks/useSSEGeneration";
import {
  toSaveDefaultProp,
  useAISummaryLanguage,
} from "@/features/player-detail/hooks/useAISummary";
import {
  cleanSummaryOutput,
  formatLastUpdated,
  generatePlayerPrompt,
  loadCachedSummary,
  saveLastGeneratedLanguage,
  saveSummaryToCache,
} from "@/features/player-detail/utils";
import AIPlayerSummaryEmptyState from "@/features/player-detail/components/molecules/AIPlayerSummaryEmptyState";
import AIPlayerSummaryErrorState from "@/features/player-detail/components/molecules/AIPlayerSummaryErrorState";
import AIPlayerSummaryLoadingState from "@/features/player-detail/components/molecules/AIPlayerSummaryLoadingState";
import SummaryContent from "@/features/player-detail/components/molecules/SummaryContent";
import AIPlayerSummaryView from "./AIPlayerSummaryView";

import { useAuth, useCurrentUser } from "@/features/auth";
import { buildPlayerSummaryRequest } from "@/features/knowledge-graph/utils/authUtils";
import {
  type PlayerDetailPlayerMetaFragment,
  usePlayerDetailGetPlayerKanbanStatusQuery,
} from "@/generated/graphql";
import { env } from "@/lib/env";
import { useTheme } from "@/providers/ThemeProvider";

/**
 * Kanban phase data structure from GraphQL query
 */
interface KanbanPhaseData {
  id: string;
  name: string;
  position: number;
  cards: {
    id: string;
    player?: {
      id: string;
    } | null;
  }[];
}

/**
 * Kanban card from phase
 */
interface KanbanCardData {
  id: string;
  player?: {
    id: string;
  } | null;
}

/**
 * Status messages displayed during AI summary generation
 * Cycles through these messages to show progress
 */
const LOADING_STATUS_MESSAGES = [
  "Thinking...",
  "Searching database...",
  "Analyzing player data...",
  "Comparing metrics...",
  "Generating insights...",
] as const;

/**
 * Props interface for AIPlayerSummaryContainer component
 */
interface AIPlayerSummaryContainerProps {
  player: PlayerDetailPlayerMetaFragment;
  isSmallScreen: boolean;
}

/**
 * Container component for AI-generated player summaries.
 *
 * Manages the lifecycle of AI summary generation using Server-Sent Events (SSE)
 * for real-time streaming. The component trusts React's natural rendering cycle
 * and backend completion events rather than enforcing artificial delays.
 * Persists the last-generated language per player to AsyncStorage so the
 * correct cached summary survives component remounts across tab navigation (SE-4074).
 *
 * Only includes scout reports the current user authored (canEdit === true) in the
 * AI prompt. Reports from other scouts that haven't been shared with the user are
 * excluded to respect access boundaries.
 * @remarks
 * State management follows a simplified approach:
 * - Loading state (`isGenerating`) follows natural generation lifecycle
 * - Language preference is owned by `useAISummaryLanguage` (SE-6506), the one
 *   AI-summary language state machine, shared with `useAISummary`
 * - Content renders naturally via React's setState and rendering cycle
 * - No artificial minimum durations or render buffers
 *
 * This design trusts:
 * - Backend [DONE] events for completion timing
 * - React's efficient rendering (typically < 16ms per frame at 60fps)
 * - useSSEGeneration hook's built-in retry and error handling
 * - react-native-sse library's automatic reconnection (pollingInterval)
 *
 * Key behaviors:
 * - `generateSummary()`: Starts SSE generation, sets `isGenerating` to true
 * - `handleGenerationComplete()`: Called when backend sends [DONE] event, immediately
 *   updates content and sets `isGenerating` to false (trusts React's fast rendering)
 * - `handleLanguageChange()`: from the shared language hook — sets the
 *   per-summary override and triggers a cache reload via useEffect. Since
 *   SE-6511 it writes NO profile; promoting the override to the account default
 *   is the separate, explicit `saveAsDefault` action
 * - Summary caching: Uses AsyncStorage with player ID + language as composite key
 * - Error handling: Captured by Sentry, displayed to user, no console statements
 *
 * Architecture rationale:
 * Previous implementation used artificial delays (500ms minimum duration, 100ms render
 * buffer) and race condition flags to prevent perceived UI glitches. After analyzing
 * the chat refactor project (refactor-chat), we learned that:
 * - React's rendering is fast enough (< 16ms) that users don't perceive delays
 * - Manual timeouts create race conditions with backend completion events
 * - Local state ownership eliminates race conditions with profile sync
 * - Trusting the library and backend simplifies code and improves reliability
 * @param props - Component props
 * @param props.player - Player data fragment from GraphQL
 * @param props.isSmallScreen - Whether the screen is small (mobile)
 * @see useSSEGeneration - Hook handling SSE connection and streaming
 */
const AIPlayerSummaryContainer: React.FC<AIPlayerSummaryContainerProps> = ({
  player,
  isSmallScreen,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [summaryContent, setSummaryContent] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  // Guards the cache-load effect below against a generation that starts or
  // completes while the cache read is still in flight (an AsyncStorage read
  // racing an SSE round trip -- not the artificial-timer kind of race
  // condition this file's architecture notes deliberately avoid). Bumped
  // whenever a generation supersedes whatever the cache effect is loading;
  // the effect only applies its result if nothing newer has happened since
  // it started.
  const latestUpdateSeqRef = useRef(0);

  const currentUser = useCurrentUser();
  const auth = useAuth();
  const { resolvedTheme } = useTheme();

  const accessiblePlayer = useMemo(
    () => ({
      ...player,
      scoutReports: player.scoutReports?.filter(r => r?.canEdit === true) ?? [],
    }),
    [player]
  );

  const scoutReportCount = accessiblePlayer.scoutReports?.length || 0;
  const isDark = resolvedTheme === "dark";

  /*
   * The AI-summary language state machine lives in `useAISummaryLanguage`
   * (SE-6506). This container used to keep its own copy — its own state, its
   * own `updateMyProfile`, its own label memo, its own restore and profile-sync
   * effects — which is how the SE-4074 fix ended up in one copy and not the
   * other. There is now exactly one implementation and both consumers inherit it.
   */
  const {
    selectedLanguage: localSelectedLanguage,
    pickerLocale,
    supportedLanguages,
    selectLanguage: handleLanguageChange,
    saveAsDefault,
    saveDefaultState,
  } = useAISummaryLanguage(player.id);

  const saveAsDefaultProp = useMemo(
    () => toSaveDefaultProp(saveDefaultState, saveAsDefault),
    [saveDefaultState, saveAsDefault]
  );

  // Query for player's Kanban status - optional, don't let failures break AI summaries
  const { data: kanbanData, error: kanbanError } =
    usePlayerDetailGetPlayerKanbanStatusQuery({
      errorPolicy: "ignore", // Don't throw errors, just return null
    });

  // Helper to find player card in a phase
  const findPlayerInPhase = useCallback(
    (phase: KanbanPhaseData, boardName: string) => {
      const playerCard = phase.cards.find(
        (card: KanbanCardData) => card.player?.id === player.id
      );
      return playerCard ? { phaseName: phase.name, boardName } : null;
    },
    [player.id]
  );

  // Extract player's current Kanban phase - simplified and robust
  const playerKanbanPhase = useMemo(() => {
    // Ignore Kanban if there's an error or no data - don't let it break AI summaries
    if (kanbanError || !kanbanData?.listMyKanbanBoards) {
      return null;
    }

    try {
      const result = kanbanData.listMyKanbanBoards.edges
        .map(edge => edge.node)
        .flatMap(board =>
          board.phases.map(phase => findPlayerInPhase(phase, board.name))
        )
        .find(result => result !== null);

      return result || null;
    } catch (error) {
      console.warn("Failed to extract Kanban phase for player:", error);
      return null;
    }
  }, [kanbanData, kanbanError, findPlayerInPhase]);

  // Handle content updates - clean output as it streams
  const handleContentUpdate = useCallback((content: string) => {
    const cleaned = cleanSummaryOutput(content);
    setSummaryContent(cleaned);
  }, []);

  // Handle generation completion
  const handleGenerationComplete = useCallback(
    async (finalContent: string, metadata: GenerationMetadata) => {
      // Supersede any cache read the loadCache effect below still has in
      // flight -- a completed generation is always fresher than a cached
      // read that started before it finished.
      latestUpdateSeqRef.current += 1;
      const cleaned = cleanSummaryOutput(finalContent);
      setSummaryContent(cleaned);

      // Use metadata.language instead of state to ensure correct cache key
      const timestamp = await saveSummaryToCache(
        metadata.playerId,
        cleaned,
        metadata.language
      );
      await saveLastGeneratedLanguage(metadata.playerId, metadata.language);
      setLastUpdated(timestamp);

      // Stop spinner immediately - React's rendering is fast enough (< 16ms at 60fps)
      setIsGenerating(false);
    },
    [] // No dependencies needed
  );

  // Handle generation errors - show error state instead of fallback content
  const handleGenerationError = useCallback((errorMsg: string) => {
    // If errorMsg is empty, this is just clearing the error, not setting one
    if (!errorMsg) {
      setError(null);
      return;
    }

    // Clear any existing content and show error immediately
    setSummaryContent("");
    setError(errorMsg);
    setIsGenerating(false);
  }, []);

  // SSE generation hook
  const { generateWithSSE, disconnect } = useSSEGeneration({
    onContentUpdate: handleContentUpdate,
    onComplete: handleGenerationComplete,
    onError: handleGenerationError,
    onGeneratingChange: setIsGenerating,
  });

  // Generate AI summary
  const generateSummary = useCallback(
    async (forceRefresh = false, languageOverride?: string) => {
      const language = languageOverride || localSelectedLanguage;

      // Defensive validation checks
      if (!player || !player.id) {
        handleGenerationError(
          "Invalid player data. Please try refreshing the page."
        );
        return;
      }

      // Check required environment configuration. This is the variable the
      // stream actually dials (`useSSEGeneration`); it used to guard on the
      // retired API Gateway WebSocket variable instead, so a build with only
      // the SSE endpoint configured refused to stream (SE-8432).
      const sseEndpoint = env.EXPO_PUBLIC_SSE_ENDPOINT;
      if (!sseEndpoint) {
        handleGenerationError(
          "Service configuration error. Please contact support."
        );
        Sentry.captureMessage("SSE endpoint not configured", {
          level: "error",
          tags: { context: "ai-summary-config-missing" },
        });
        return;
      }

      // Guard against concurrent generations
      // Skip guard check if forceRefresh is true (user explicitly requested refresh)
      if (!forceRefresh && isGenerating) {
        return;
      }

      // SE-5426/SE-5427: authenticated-only; skip until the real org resolves.
      if (!currentUser?.organizationId) {
        return;
      }

      // Check cache first unless forcing refresh
      if (!forceRefresh) {
        const cached = await loadCachedSummary(player.id, language);
        if (cached) {
          setSummaryContent(cached.content);
          setLastUpdated(cached.timestamp);
          return;
        }
      }

      // Before starting SSE generation -- also supersede any cache read the
      // loadCache effect below still has in flight, so a slow AsyncStorage
      // resolution cannot later overwrite streamed/generated content.
      latestUpdateSeqRef.current += 1;
      setSummaryContent("");
      setError(null);

      const prompt = generatePlayerPrompt(
        accessiblePlayer,
        playerKanbanPhase,
        language
      );

      const request = buildPlayerSummaryRequest(
        currentUser,
        prompt,
        player.id,
        auth?.data?.accessToken
      );
      if (!request) {
        return;
      }

      // Create metadata object with language and playerId
      const metadata: GenerationMetadata = {
        language,
        playerId: player.id,
      };

      generateWithSSE(request, metadata);
    },
    [
      accessiblePlayer,
      player,
      playerKanbanPhase,
      currentUser,
      generateWithSSE,
      isGenerating,
      auth?.data?.accessToken,
      localSelectedLanguage,
      handleGenerationError,
    ]
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    // Disconnect any existing SSE connection first
    disconnect();
    // Clear content and error immediately for responsive UI
    setSummaryContent("");
    setError(null);
    // Start generation with forceRefresh flag
    generateSummary(true);
  }, [generateSummary, disconnect]);

  // Toggle expanded state
  const handleToggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Format the last updated time
  const formattedLastUpdated = useMemo(
    () => formatLastUpdated(lastUpdated),
    [lastUpdated]
  );

  // Track current status message index
  const [statusMessageIndex, setStatusMessageIndex] = useState(0);

  // Memoized components for each state
  const errorStateComponent = useMemo(
    () => (
      <AIPlayerSummaryErrorState
        error={error || ""}
        isDark={isDark}
        isSmallScreen={isSmallScreen}
        onRefresh={handleRefresh}
      />
    ),
    [error, isDark, isSmallScreen, handleRefresh]
  );

  const loadingStateComponent = useMemo(
    () => (
      <AIPlayerSummaryLoadingState
        isDark={isDark}
        isSmallScreen={isSmallScreen}
        statusMessage={LOADING_STATUS_MESSAGES[statusMessageIndex]}
      />
    ),
    [isDark, isSmallScreen, statusMessageIndex]
  );

  const emptyStateComponent = useMemo(
    () => (
      <AIPlayerSummaryEmptyState
        isDark={isDark}
        isSmallScreen={isSmallScreen}
        onRefresh={handleRefresh}
      />
    ),
    [isDark, isSmallScreen, handleRefresh]
  );

  const contentStateComponent = useMemo(
    () => (
      <SummaryContent
        testID={TEST_ID_AI_PLAYER_SUMMARY.CONTENT}
        summaryContent={summaryContent}
        isSmallScreen={isSmallScreen}
        isDark={isDark}
      />
    ),
    [summaryContent, isSmallScreen, isDark]
  );

  // Determine which content to render
  const contentComponent = useMemo(() => {
    if (error && !isGenerating) return errorStateComponent;
    if (isGenerating) return loadingStateComponent;

    // Show empty state with "Generate Summary" button when no content exists
    // This includes: no cache found, or generation completed with no content
    if (!summaryContent) return emptyStateComponent;

    return contentStateComponent;
  }, [
    error,
    isGenerating,
    summaryContent,
    errorStateComponent,
    loadingStateComponent,
    emptyStateComponent,
    contentStateComponent,
  ]);

  // Cycle through status messages during loading
  useEffect(() => {
    if (!isGenerating) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO: Refactor this
      setStatusMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setStatusMessageIndex(
        prev => (prev + 1) % LOADING_STATUS_MESSAGES.length
      );
    }, 2500); // Change message every 2.5 seconds

    return () => clearInterval(interval);
  }, [isGenerating]);

  // Load cached summary when player changes, scout reports update, or language changes
  useEffect(() => {
    // Claim the current sequence number BEFORE the async read starts. If a
    // generation starts or completes while this read is in flight, it bumps
    // latestUpdateSeqRef itself (see handleGenerationComplete / generateSummary),
    // which invalidates this closure's mySeq -- so the check below skips
    // applying a now-stale cache result over fresher, already-rendered
    // content.
    const mySeq = ++latestUpdateSeqRef.current;
    const loadCache = async () => {
      const cached = await loadCachedSummary(player.id, localSelectedLanguage);
      if (latestUpdateSeqRef.current !== mySeq) return;
      if (cached) {
        setSummaryContent(cached.content);
        setLastUpdated(cached.timestamp);
        setError(null);
      } else {
        setSummaryContent("");
        setLastUpdated(null);
        setError(null);
      }
    };
    loadCache();
  }, [player.id, scoutReportCount, localSelectedLanguage]);

  // Cleanup SSE connection on unmount to prevent state updates after unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Defensive: If we have content but no timestamp, set one
  useEffect(() => {
    if (summaryContent && !lastUpdated && !isGenerating) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO: Refactor this
      setLastUpdated(new Date().toISOString());
    }
  }, [summaryContent, lastUpdated, isGenerating]);

  return (
    <AIPlayerSummaryView
      isExpanded={isExpanded}
      isGenerating={isGenerating}
      lastUpdated={formattedLastUpdated}
      playerName={player?.name ?? ""}
      isSmallScreen={isSmallScreen}
      pickerLocale={pickerLocale}
      saveAsDefault={saveAsDefaultProp}
      isDark={isDark}
      contentComponent={contentComponent}
      onToggleExpanded={handleToggleExpanded}
      onLanguageChange={handleLanguageChange}
      onRefresh={handleRefresh}
      supportedLanguages={[...supportedLanguages]}
    />
  );
};

export default AIPlayerSummaryContainer;
